import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { tasksAPI, notificationsAPI } from '../api/api';
import NotificationsDropdown from './NotificationsDropdown';
import logoPrometey from '../logo-prometey.svg';
import './Navbar.css';

const Navbar: React.FC = () => {
  const { user, logout, logoutFromImpersonation, originalAdmin, isAdmin, isManager, isImpersonating } = useAuth();
  const [showLogoutMenu, setShowLogoutMenu] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [taskCounts, setTaskCounts] = useState({ myTasks: 0, forReview: 0 });
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);
  const menuRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const burgerButtonRef = useRef<HTMLButtonElement>(null);
  const notificationsButtonRef = useRef<HTMLButtonElement>(null);
  const location = useLocation();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowLogoutMenu(false);
      }
      if (isMobileMenuOpen && 
          mobileMenuRef.current && 
          !mobileMenuRef.current.contains(event.target as Node) &&
          burgerButtonRef.current &&
          !burgerButtonRef.current.contains(event.target as Node)) {
        setIsMobileMenuOpen(false);
      }
    };

    if (showLogoutMenu || isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showLogoutMenu, isMobileMenuOpen]);

  useEffect(() => {
    const loadCounts = async () => {
      try {
        const counts = await tasksAPI.getTaskCounts();
        setTaskCounts(counts);
      } catch (error) {
        console.error('Ошибка при загрузке счетчиков задач:', error);
      }
    };

    const loadNotificationsCount = async () => {
      try {
        const count = await notificationsAPI.getUnreadCount();
        setUnreadNotificationsCount(count);
      } catch (error) {
        console.error('Ошибка при загрузке счетчика уведомлений:', error);
      }
    };

    loadCounts();
    loadNotificationsCount();
    // Обновляем счетчики каждые 30 секунд
    const interval = setInterval(() => {
      loadCounts();
      loadNotificationsCount();
    }, 30000);
    return () => clearInterval(interval);
  }, [location.pathname, user?.id, isImpersonating]);

  const isActive = (path: string) => {
    if (path === '/boards' || path === '/') {
      return location.pathname === '/boards' || location.pathname === '/' || location.pathname.startsWith('/board/');
    }
    return location.pathname.startsWith(path);
  };

  const handleNavClick = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <nav className="navbar">
      <div className="navbar-content">
        <div className="navbar-left">
          <Link to="/boards" className="navbar-logo-container">
            <img src={logoPrometey} alt="Прометей задачи" className="navbar-logo" />
          </Link>
          <div className="nav-buttons-container">
            <Link 
              to="/boards" 
              className={`nav-button ${isActive('/boards') ? 'active' : ''}`}
            >
              Доски
            </Link>
            <Link 
              to="/my-tasks" 
              className={`nav-button ${isActive('/my-tasks') ? 'active' : ''}`}
            >
              Мои задачи
              {taskCounts.myTasks > 0 && (
                <span className="nav-badge">{taskCounts.myTasks}</span>
              )}
            </Link>
            <Link 
              to="/for-review" 
              className={`nav-button ${isActive('/for-review') ? 'active' : ''}`}
            >
              На проверке
              {taskCounts.forReview > 0 && (
                <span className="nav-badge">{taskCounts.forReview}</span>
              )}
            </Link>
            {(isAdmin || isManager) && (
              <Link 
                to="/users" 
                className={`nav-button ${isActive('/users') ? 'active' : ''}`}
              >
                Сотрудники
              </Link>
            )}
            {isAdmin && (
              <Link 
                to="/admin" 
                className={`nav-button ${isActive('/admin') ? 'active' : ''}`}
              >
                Админ панель
              </Link>
            )}
          </div>
          <div className="mobile-controls">
            <button
              ref={notificationsButtonRef}
              onClick={() => setShowNotifications(!showNotifications)}
              className="notifications-button mobile-notifications-button"
              title="Уведомления"
            >
              🔔
              {unreadNotificationsCount > 0 && (
                <span className="notifications-badge">{unreadNotificationsCount}</span>
              )}
            </button>
            {showNotifications && (
              <NotificationsDropdown
                onClose={() => setShowNotifications(false)}
                onNotificationClick={() => {
                  setUnreadNotificationsCount(prev => Math.max(0, prev - 1));
                }}
              />
            )}
            <button 
              ref={burgerButtonRef}
              className="burger-menu-button"
              onClick={(e) => {
                e.stopPropagation();
                setIsMobileMenuOpen(!isMobileMenuOpen);
              }}
              aria-label="Меню"
            >
              <span className={`burger-line ${isMobileMenuOpen ? 'open' : ''}`}></span>
              <span className={`burger-line ${isMobileMenuOpen ? 'open' : ''}`}></span>
              <span className={`burger-line ${isMobileMenuOpen ? 'open' : ''}`}></span>
            </button>
          </div>
        </div>
        <div className="navbar-user">
          <div className="notifications-container">
            <button
              ref={notificationsButtonRef}
              onClick={() => setShowNotifications(!showNotifications)}
              className="notifications-button desktop-notifications-button"
              title="Уведомления"
            >
              🔔
              {unreadNotificationsCount > 0 && (
                <span className="notifications-badge">{unreadNotificationsCount}</span>
              )}
            </button>
            {showNotifications && (
              <NotificationsDropdown
                onClose={() => setShowNotifications(false)}
                onNotificationClick={async () => {
                  // Перезагружаем счетчик уведомлений
                  try {
                    const count = await notificationsAPI.getUnreadCount();
                    setUnreadNotificationsCount(count);
                  } catch (error) {
                    console.error('Ошибка при обновлении счетчика уведомлений:', error);
                    setUnreadNotificationsCount(prev => Math.max(0, prev - 1));
                  }
                }}
              />
            )}
          </div>
          <div className="user-info-button">
            <span className="user-icon">👤</span>
            <span className="user-name">{user?.username || 'admin'}</span>
          </div>
          <div className="logout-menu-container" ref={menuRef}>
            <button 
              onClick={() => {
                if (isImpersonating) {
                  setShowLogoutMenu(!showLogoutMenu);
                } else {
                  logout();
                }
              }}
              className="logout-btn"
            >
              Выйти
            </button>
            {showLogoutMenu && isImpersonating && (
              <div className="logout-menu">
                <button 
                  onClick={async () => {
                    await logoutFromImpersonation();
                    setShowLogoutMenu(false);
                  }}
                  className="logout-menu-item"
                >
                  ← Вернуться к администратору
                </button>
                <button 
                  onClick={() => {
                    logout();
                    setShowLogoutMenu(false);
                  }}
                  className="logout-menu-item logout-danger"
                >
                  Выйти полностью
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      <div 
        className={`mobile-menu ${isMobileMenuOpen ? 'open' : ''}`}
        ref={mobileMenuRef}
      >
        <div className="mobile-menu-user-section">
          <div className="mobile-user-info">
            <span className="mobile-user-icon">👤</span>
            <span className="mobile-user-name">{user?.username || 'admin'}</span>
          </div>
        </div>
        <Link 
          to="/boards" 
          className={`mobile-nav-button ${isActive('/boards') ? 'active' : ''}`}
          onClick={handleNavClick}
        >
          Доски
        </Link>
        <Link 
          to="/my-tasks" 
          className={`mobile-nav-button ${isActive('/my-tasks') ? 'active' : ''}`}
          onClick={handleNavClick}
        >
          Мои задачи
          {taskCounts.myTasks > 0 && (
            <span className="nav-badge">{taskCounts.myTasks}</span>
          )}
        </Link>
        <Link 
          to="/for-review" 
          className={`mobile-nav-button ${isActive('/for-review') ? 'active' : ''}`}
          onClick={handleNavClick}
        >
          На проверке
          {taskCounts.forReview > 0 && (
            <span className="nav-badge">{taskCounts.forReview}</span>
          )}
        </Link>
        {(isAdmin || isManager) && (
          <Link 
            to="/users" 
            className={`mobile-nav-button ${isActive('/users') ? 'active' : ''}`}
            onClick={handleNavClick}
          >
            Сотрудники
          </Link>
        )}
        {isAdmin && (
          <Link 
            to="/admin" 
            className={`mobile-nav-button ${isActive('/admin') ? 'active' : ''}`}
            onClick={handleNavClick}
          >
            Админ панель
          </Link>
        )}
        <div className="mobile-menu-logout-section">
          {isImpersonating && (
            <>
              <button 
                onClick={async () => {
                  await logoutFromImpersonation();
                  setIsMobileMenuOpen(false);
                }}
                className="mobile-logout-menu-item"
              >
                ← Вернуться к администратору
              </button>
              <button 
                onClick={() => {
                  logout();
                  setIsMobileMenuOpen(false);
                }}
                className="mobile-logout-menu-item mobile-logout-danger"
              >
                Выйти полностью
              </button>
            </>
          )}
          {!isImpersonating && (
            <button 
              onClick={() => {
                logout();
                setIsMobileMenuOpen(false);
              }}
              className="mobile-logout-btn"
            >
              Выйти
            </button>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;

